import { IMarket } from 'data/schema';
import { user } from 'utils/auth/user';
import { getCountryISO2 } from 'utils/isoConverter';

export const defaultLanguageFor = (market: IMarket): string => orderedLanguagesPerMarket[market]?.[0] ?? 'en';

export const orderedLanguagesPerMarket: Partial<Record<IMarket, string[]>> = {
  ['ARE']: ['en'],
  ['ARG']: ['es_AR'],
  ['AUS']: ['en_AU'],
  ['AUT']: ['de_AT'],
  ['BEL']: ['fr_BE', 'nl_BE', 'fr', 'nl'],
  ['BHR']: ['en_BH'],
  ['BIH']: ['bs'],
  ['BOL']: ['es_BO'],
  ['BRA']: ['pt_BR'],
  ['CAN']: ['en_CA', 'fr_CA'],
  ['CHE']: ['de_CH', 'it_CH', 'fr_CH'],
  ['CHL']: ['es_CL'],
  ['CHN']: ['zh_CN'],
  ['COL']: ['es_CO'],
  ['CRI']: ['es_CR'],
  ['CUW']: ['en_CW', 'nl'],
  ['CYM']: ['en'],
  ['CYP']: ['en_GB'],
  ['DEU']: ['de'],
  ['DNK']: ['da'],
  ['DOM']: ['es_DO'],
  ['ECU']: ['es_EC'],
  ['EGY']: ['en'],
  ['ESP']: ['es'],
  ['EST']: ['et'],
  ['FIN']: ['fi', 'sv_FI'],
  ['FRA']: ['fr'],
  ['GBR']: ['en_GB'],
  ['GRC']: ['el'],
  ['GLP']: ['fr_GP'],
  ['GTM']: ['es_GT'],
  ['GUF']: ['fr_GF'],
  ['IND']: ['en'],
  ['IRL']: ['en_IE', 'en'],
  ['ITA']: ['it'],
  ['JAM']: ['en_JM', 'en'],
  ['JOR']: ['en'],
  ['JPN']: ['ja'],
  ['KOR']: ['ko'],
  ['KWT']: ['en_KW'],
  ['LBN']: ['en_LB'],
  ['LCA']: ['en_LC', 'en'],
  ['LTU']: ['lt', 'ru'],
  ['LUX']: ['fr_LU', 'de_LU'],
  ['LVA']: ['lv', 'ru'],
  ['MEX']: ['es_MX'],
  ['MTQ']: ['en_MQ'],
  ['NLD']: ['nl'],
  ['NOR']: ['no'],
  ['NZL']: ['en'],
  ['OMN']: ['en'],
  ['PAN']: ['es_PA'],
  ['PER']: ['es_PE'],
  ['POL']: ['pl'],
  ['PRY']: ['es_PR'],
  ['ROU']: ['ro'],
  ['QAT']: ['en_QA'],
  ['SAU']: ['en'],
  ['SGP']: ['en_SG'],
  ['SLV']: ['es_SV'],
  ['SRB']: ['sr'],
  ['SVK']: ['sk'],
  ['SWE']: ['sv'],
  ['SXM']: ['en_SX', 'nl'],
  ['TTO']: ['en_TT', 'en'],
  ['TUR']: ['tr'],
  ['TWN']: ['zh_TW'],
  ['URY']: ['es_UY'],
  ['USA']: ['en_US'],
  ['ZAF']: ['en_ZA'],
};

export const marketToTwoLettercode: Partial<Record<IMarket, string>> = {
  ['ARE']: 'AE',
  ['ARG']: 'AR',
  ['AUS']: 'AU',
  ['AUT']: 'AT',
  ['BEL']: 'BE',
  ['BHR']: 'BH',
  ['BIH']: 'BA',
  ['BOL']: 'BO',
  ['BRA']: 'BR',
  ['CAN']: 'CA',
  ['CHE']: 'CH',
  ['CHL']: 'CL',
  ['CHN']: 'CN',
  ['COL']: 'CO',
  ['CRI']: 'CR',
  ['CUW']: 'CW',
  ['CYM']: 'KY',
  ['CYP']: 'CY',
  ['DEU']: 'DE',
  ['DNK']: 'DK',
  ['DOM']: 'DO',
  ['ECU']: 'EC',
  ['EGY']: 'EG',
  ['ESP']: 'ES',
  ['EST']: 'EE',
  ['FIN']: 'FI',
  ['FRA']: 'FR',
  ['GBR']: 'GB',
  ['GRC']: 'GR',
  ['GLP']: 'GP',
  ['GTM']: 'GT',
  ['GUF']: 'GF',
  ['IND']: 'IN',
  ['IRL']: 'IE',
  ['ITA']: 'IT',
  ['JAM']: 'JM',
  ['JOR']: 'JO',
  ['JPN']: 'JP',
  ['KOR']: 'KR',
  ['KWT']: 'KW',
  ['LBN']: 'LB',
  ['LCA']: 'LC',
  ['LTU']: 'LT',
  ['LUX']: 'LU',
  ['LVA']: 'LV',
  ['MEX']: 'MX',
  ['MTQ']: 'MQ',
  ['NLD']: 'NL',
  ['NOR']: 'NO',
  ['NZL']: 'NZ',
  ['OMN']: 'OM',
  ['PAN']: 'PA',
  ['PER']: 'PE',
  ['POL']: 'PL',
  ['ROU']: 'RO',
  ['PRY']: 'PY',
  ['QAT']: 'QA',
  ['SAU']: 'SA',
  ['SGP']: 'SG',
  ['SLV']: 'SV',
  ['SRB']: 'RS',
  ['SVK']: 'SK',
  ['SWE']: 'SE',
  ['SXM']: 'SX',
  ['TTO']: 'TT',
  ['TUR']: 'TR',
  ['TWN']: 'TW',
  ['URY']: 'UY',
  ['USA']: 'US',
  ['ZAF']: 'ZA',
};

export function getLocale(market: IMarket = user.market.iso3 as IMarket): string {
  if (!market) return 'en';

  const browserLanguages = [...new Set(navigator.languages.map((language: string) => language.replaceAll('-', '_')))];
  const availableLanguages = orderedLanguagesPerMarket[market];

  if (!availableLanguages) {
    return 'en';
  }

  // First, try exact match in browser order
  const exactMatch = browserLanguages.find((browserLang) => availableLanguages.includes(browserLang));
  if (exactMatch) {
    return exactMatch;
  }

  // Second, match base language to regional variant (e.g., 'de' -> 'de_CH')
  const baseMatch = browserLanguages
    .map((browserLang) => {
      const baseLang = browserLang.split('_')[0];
      return availableLanguages.find((availableLang) => availableLang.split('_')[0] === baseLang);
    })
    .find(Boolean);

  return baseMatch || defaultLanguageFor(market) || 'en';
}

/**
 * Get the locale code for the current market in [ISO 639]_[ISO 3166]
 * e.g. en_US, de_DE, fr_FR
 */
export function getLocaleCode(market?: IMarket) {
  let iso639: string, iso3166: string | boolean;
  const locale = getLocale(market);
  // eslint-disable-next-line prefer-const
  [iso639, iso3166] = locale.split('_');
  if (iso639 && iso3166 && Object.values(marketToTwoLettercode).indexOf(iso3166) !== -1) {
    return locale;
  }
  const country = getCountryISO2(market) ?? user.market.iso2;
  iso3166 = typeof country === 'string' && country.toLocaleUpperCase();

  if (!iso3166 || !iso639) {
    throw new Error(`Invalid locale code. iso639: ${iso639}, iso3166: ${iso3166}`);
  }

  const code = `${iso639}_${iso3166}`;
  return code;
}

export function getLanguageName(langCode: string, locale: string = 'en'): string {
  const displayNames = new Intl.DisplayNames([locale], { type: 'language' });
  return displayNames.of(langCode) || langCode;
}
